import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createIcons, icons } from 'lucide';
import { DocCategory, CategoryConfig, DocumentEntry, DocsManifest } from '../types/docs';
import SEO from '../SEO';

const CATEGORY_CONFIG: Record<DocCategory, CategoryConfig> = {
  articles: {
    label: 'Articles',
    icon: 'newspaper',
    color: '#FFAD60',
    bgColor: 'rgba(255,173,96,0.15)',
    borderColor: 'rgba(255,173,96,0.3)',
    sectionTitle: 'Magazine Articles',
    sectionDescription: 'Industry insights, best practices, and updates from our team',
  },
  scientific: {
    label: 'Scientific',
    icon: 'beaker',
    color: '#a259ff',
    bgColor: 'rgba(162,89,255,0.15)',
    borderColor: 'rgba(162,89,255,0.3)',
    sectionTitle: 'Scientific Articles',
    sectionDescription: 'Peer-reviewed research and advanced technical studies',
  },
  marketing: {
    label: 'Marketing',
    icon: 'zap',
    color: '#3b82f6',
    bgColor: 'rgba(59,130,246,0.15)',
    borderColor: 'rgba(59,130,246,0.3)',
    sectionTitle: 'Brouchures & Flyers',
    sectionDescription: 'Flyers, brochures, and quick-start guides',
  },
  technical: {
    label: 'Technical',
    icon: 'cpu',
    color: '#9ED3CD',
    bgColor: 'rgba(158,211,205,0.15)',
    borderColor: 'rgba(158,211,205,0.3)',
    sectionTitle: 'Technical Reports',
    sectionDescription: 'System architecture, API documentation, and security reports',
  },
  posts: {
    label: 'Posts',
    icon: 'message-square',
    color: '#f472b6',
    bgColor: 'rgba(244,114,182,0.15)',
    borderColor: 'rgba(244,114,182,0.3)',
    sectionTitle: 'Posts',
    sectionDescription: 'News, updates, and announcements from the AquaWARE team',
  },
};

const CATEGORY_ORDER: DocCategory[] = ['posts', 'articles', 'scientific', 'marketing', 'technical'];

const LINKEDIN_POSTS = [
  {
    id: 'post-4',
    title: 'Explore Forecasting in AquaWARE',
    body: `In recirculating aquaculture, water quality is never static: feeding, biomass, biofilter activity, degassing, temperature, and chemistry are constantly interacting. Forecasting turns this complexity into operational foresight — showing not only where CO₂, pH, alkalinity, TIC, and related risks are now, but where they are likely heading in the coming hours and days. This gives operators time to act before small chemical shifts develop into stressful conditions or elevated sulfide risk, helping protect fish welfare, stabilize production, and make water treatment decisions with confidence.`,
    date: '2026-05-21',
    video: null,
    image: '/images/AQW_forecast.png',
    modalVideo: null,
    readMore: null,
  },
  {
    id: 'post-1',
    title: 'Monitoring is common, understanding is not',
    body: `In RAS facilities data isn't the problem. Understanding is. Is it noise — or an early sign of risk?\nWhat is changing? Why is changing?\nMonitoring is common.\nUnderstanding is not.\nStay tuned, AquaWARE`,
    date: '2026-05-20',
    video: '/videos/AquaWARE_linkedin_2_3_lowRes_web.mp4',
    image: null,
    modalVideo: null,
    readMore: null,
  },
  {
    id: 'post-2',
    title: 'Hidden Sulfide Dynamics in RAS',
    body: `How Metabolic Load and Biological Stress Control Hydrogen Sulfide Risk\n\nSub-microgram per liter H₂S monitoring in a commercial Atlantic salmon RAS | Feeding-driven CO₂ pulses unlock toxic H₂S via pH-mediated speciation shifts | H₂S response is regime-dependent above ~1% body weight per day feeding rate | Compartment mass-balance localizes routine sulfide production to the biofilter | Stress event suggests endogenous biological H₂S release from the salmon biomass`,
    date: '2026-05-20',
    video: '/videos/H2S_Evolution.mp4',
    image: null,
    modalVideo: '/videos/H2S_Evolution.mp4',
    readMore: { label: 'Hidden Sulfide Dynamics in Recirculating Aquaculture Systems', path: '/docs/scientific/Hidden Sulfide Dynamics in Recirculating Aquaculture Systems.pdf' },
  },
  {
    id: 'post-3',
    title: 'High Resolution H₂S data in AquaWARE',
    body: `Within AquaSENSE, the H₂S sensor is capable of resolving concentrations below 0.1 μg/L with minute-scale temporal resolution, enabling the detection of low-amplitude fluctuations that are inaccessible with conventional grab-sampling approaches.\n\n That high-resolution monitoring fundamentally provides a new basis for linking feeding strategies, system operation, and water quality control, and highlights the importance of early detection of transitions toward elevated H2S risk. Low-level monitoring is therefore not a precision luxury, but a prerequisite for detecting the "H₂S Latent Reservoir" before it is chemically released.`,
    date: '2026-05-20',
    video: null,
    image: '/images/HiRes_H2S.png',
    modalVideo: null,
    readMore: { label: 'Hidden Sulfide Dynamics in Recirculating Aquaculture Systems', path: '/docs/scientific/Hidden Sulfide Dynamics in Recirculating Aquaculture Systems.pdf' },
  },
  {
    id: 'post-5',
    title: "AquaCARE - SaaS in HavEXPO'26",
    body: `At HavEXPO, Eivind Farestveit Larsen (CEO) went beyond the sensors — explaining why high-resolution data is the backbone of modern RAS management, how to store it properly, and how to turn it into real operational insight, illustrated through compelling real-world case studies.`,
    date: '2026-05-07',
    video: null,
    image: '/team/Eivind_HavEXPO.jpg',
    modalVideo: null,
    readMore: null,
  },
];

const LinkedInPostCard: React.FC<{ post: typeof LINKEDIN_POSTS[0] }> = ({ post }) => {
  const [videoModalOpen, setVideoModalOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const formattedDate = new Date(post.date).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const cfg = CATEGORY_CONFIG['posts'];

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.body.split('\n')[0],
      url: window.location.href,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (_) {}
    } else {
      await navigator.clipboard.writeText(`${post.title}\n\n${shareData.text}\n\n${window.location.href}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div className="plan-card" style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Video or Image */}
        <div style={{ width: '100%', paddingBottom: '75%', background: '#000', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
          {post.video && (
            <video
              src={post.video}
              autoPlay muted loop playsInline
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          {post.image && (
            <img
              src={post.image}
              alt={post.title}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          {post.modalVideo && (
            <button
              onClick={() => setVideoModalOpen(true)}
              title="Watch full video"
              style={{
                position: 'absolute', bottom: 8, right: 8,
                background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                color: '#fff', fontSize: 11, fontWeight: 600, backdropFilter: 'blur(4px)',
              }}
            >
              <i data-lucide="maximize-2" style={{ width: 12, height: 12 }} />
              Watch
            </button>
          )}
        </div>
        {/* Content */}
        <div style={{ padding: '20px 20px 16px', display: 'flex', flexDirection: 'column', flex: 1, textAlign: 'left' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 10px', color: 'var(--text)', lineHeight: 1.4 }}>
            {post.title}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 14px', flex: 1, lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {post.body}
          </p>
          {post.readMore && (
            <a
              href={post.readMore.path}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'transparent', color: cfg.color,
                border: `1px solid ${cfg.color}`,
                borderRadius: 8, padding: '10px 12px',
                fontSize: 13, fontWeight: 600, textDecoration: 'none',
                transition: 'all 0.2s', margin: '0 0 12px',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = cfg.color;
                (e.currentTarget as HTMLAnchorElement).style.color = '#1e2938';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = cfg.color;
              }}
            >
              <i data-lucide="book-open" style={{ width: 14, height: 14 }} />
              Read more
            </a>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
              {formattedDate}
            </p>
            <button
              onClick={handleShare}
              title="Share"
              style={{
                background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                color: copied ? '#9ED3CD' : 'var(--muted)', fontSize: 11,
                transition: 'all 0.2s',
              }}
            >
              <i data-lucide={copied ? 'check' : 'share-2'} style={{ width: 12, height: 12 }} />
              {copied ? 'Link copied!' : 'Share'}
            </button>
          </div>
        </div>
      </div>

      {/* Video modal */}
      {videoModalOpen && post.modalVideo && (
        <div
          onClick={() => setVideoModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'relative', width: '100%', maxWidth: 860,
              background: '#0a0e1a', borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 32px 64px rgba(0,0,0,0.6)', overflow: 'hidden',
            }}
          >
            <button
              onClick={() => setVideoModalOpen(false)}
              style={{
                position: 'absolute', top: 12, right: 14, zIndex: 10,
                background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%',
                width: 32, height: 32, cursor: 'pointer', color: '#fff',
                fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >×</button>
            <video
              src={post.modalVideo}
              controls autoPlay
              style={{ width: '100%', height: 'auto', display: 'block', background: '#000' }}
            />
          </div>
        </div>
      )}
    </>
  );
};

interface DocumentCardProps {
  doc: DocumentEntry;
  config: CategoryConfig;
  onOpen: (doc: DocumentEntry) => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ doc, config, onOpen }) => {
  const formattedDate = new Date(doc.date).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  return (
    <div className="plan-card" style={{ padding: 24, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: config.bgColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16, flexShrink: 0,
      }}>
        <i data-lucide={config.icon} style={{ width: 28, height: 28, color: config.color }} />
      </div>
      <div style={{
        width: '100%', height: 130, borderRadius: 10, overflow: 'hidden',
        marginBottom: 16, position: 'relative', background: '#f0f0f0', flexShrink: 0,
      }}>
        <iframe
          src={`${doc.path}#page=1&toolbar=0&navpanes=0&scrollbar=0`}
          title={`Preview of ${doc.title}`}
          style={{
            width: '200%', height: '280%', border: 'none',
            transform: 'scale(0.5)', transformOrigin: 'top left',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'absolute', inset: 0 }} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 500, margin: '0 0 8px', color: 'var(--text)', lineHeight: 1.4 }}>
        {doc.title}
      </h3>
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px', flex: 1, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
        {doc.description}
      </p>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: '0 0 16px' }}>
        {formattedDate}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => onOpen(doc)}
          style={{
            flex: 1, background: 'transparent', color: config.color,
            fontWeight: 600, border: `1px solid ${config.color}`,
            borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
            fontSize: 13, transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = config.color;
            (e.currentTarget as HTMLButtonElement).style.color = '#1e2938';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = config.color;
          }}
        >
          <i data-lucide="eye" style={{ width: 14, height: 14 }} />
          View
        </button>
        <a
          href={doc.path}
          download={doc.filename}
          style={{
            background: 'transparent', color: 'var(--muted)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
            fontSize: 13, transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: 6,
            textDecoration: 'none',
          }}
          title="Download"
        >
          <i data-lucide="download" style={{ width: 14, height: 14 }} />
        </a>
      </div>
    </div>
  );
};

const Media = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<DocumentEntry | null>(null);
  const sectionRefs = useRef<Record<DocCategory, HTMLDivElement | null>>({
    articles: null, scientific: null, marketing: null, technical: null, posts: null,
  });

  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    fetch('/docs/docs-manifest.json')
      .then(r => r.json())
      .then((data: DocsManifest) => {
        setDocuments(data.documents);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    createIcons({ icons });
  }, [documents, activeModal]);

  useEffect(() => {
    createIcons({ icons });
  }, []);

  const scrollToSection = (cat: DocCategory) => {
    sectionRefs.current[cat]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEO
        title="Media Library"
        description="Browse AquaWARE's media library — posts, articles, scientific papers, brochures and technical resources on aquaculture, water quality and digital fish farming."
        path="/media"
      />
      <section className='doc-section' style={{ paddingTop: 64 }}>
        {/* Hero banner */}
        <div style={{
          position: 'relative', width: '100%', overflow: 'hidden',
          backgroundImage: 'url(/images/banners_AQW_page_library.webp)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          padding: '64px 48px 56px',
          minHeight: 360,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        }}>
          {/* Dark overlay for text readability */}
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(to top, rgba(10,10,12,0.82) 0%, rgba(10,10,12,0.45) 60%, rgba(10,10,12,0.3) 100%)',
          }} />
          {/* Back button — anchored top-left, outside text alignment */}
          <button
            className="back-home-btn"
            onClick={() => navigate('/')}
            style={{
              position: 'absolute', top: 20, left: 48, zIndex: 2,
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
              cursor: 'pointer', alignItems: 'center', gap: 8,
              color: '#fff', fontSize: 13, padding: '6px 14px', borderRadius: 8,
            }}
          >
            <i data-lucide="arrow-left" style={{ width: 16, height: 16 }} /> Back to Home
          </button>
          {/* Title block — centered */}
          <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: '#9ED3CD',
              margin: '0 0 12px', fontFamily: "'JetBrains Mono', monospace",
            }}>// knowledge base</p>
            <h1 style={{ margin: '0 0 14px' }}>
              AquaWARE Library
            </h1>
            <p style={{ color: 'var(--muted)', fontSize: 15, margin: 0 }}>
              Articles · Scientific Research · Marketing · Technical Reports
            </p>
          </div>
        </div>
        <div
          id="documentsPage"
          className="page-container active"
          style={{ width: '100%' }}
        >
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px' }}>
            <div style={{ marginBottom: 60 }}>
              <h1 className="serif" style={{ fontSize: 'clamp(32px, 5vw, 48px)', margin: '0 0 12px', fontWeight: 400 }}>
                Documents & News
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 16, margin: 0, textAlign: 'left' }}>
                Browse our collection of resources, case studies, and technical documentation organized by type.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
                {CATEGORY_ORDER.map(cat => {
                  const cfg = CATEGORY_CONFIG[cat];
                  return (
                    <button
                      key={cat}
                      onClick={() => scrollToSection(cat)}
                      style={{
                        background: cfg.bgColor, border: `1px solid ${cfg.borderColor}`,
                        color: cfg.color, padding: '8px 16px', borderRadius: 8,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >
                      <i data-lucide={cfg.icon} style={{ width: 14, height: 14, display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {CATEGORY_ORDER.map(cat => {
              const cfg = CATEGORY_CONFIG[cat];
              const docs = documents.filter(d => d.category === cat);
              return (
                <div key={cat} style={{ marginBottom: 80 }}>
                  <div
                    ref={el => { sectionRefs.current[cat] = el; }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}
                  >
                    <i data-lucide={cfg.icon} style={{ width: 28, height: 28, color: cfg.color }} />
                    <h2 className="serif" style={{ fontSize: 28, fontWeight: 400, margin: 0, color: 'var(--text)' }}>
                      {cfg.sectionTitle}
                    </h2>
                  </div>
                  <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 36px' }}>
                    {cfg.sectionDescription}
                  </p>
                  {isLoading && (
                    <p style={{ color: 'var(--muted)', fontSize: 14 }}>Loading…</p>
                  )}
                  {cat === 'posts' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
                      {LINKEDIN_POSTS.map(post => (
                        <LinkedInPostCard key={post.id} post={post} />
                      ))}
                    </div>
                  )}
                  {cat !== 'posts' && !isLoading && docs.length === 0 && (
                    <p style={{ color: 'var(--muted)', fontSize: 14, fontStyle: 'italic' }}>No documents in this category yet.</p>
                  )}
                  {cat !== 'posts' && !isLoading && docs.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
                      {docs.map(doc => (
                        <DocumentCard key={doc.id} doc={doc} config={cfg} onOpen={setActiveModal} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {activeModal && (() => {
        const cfg = CATEGORY_CONFIG[activeModal.category];
        return (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 24,
            }}
            onClick={() => setActiveModal(null)}
          >
            <div
              style={{
                background: 'var(--surface)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 20, width: '100%', maxWidth: 900,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: cfg.bgColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i data-lucide={cfg.icon} style={{ width: 18, height: 18, color: cfg.color }} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: cfg.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {cfg.label}
                    </p>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--text)' }}>
                      {activeModal.title}
                    </h3>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <a
                    href={activeModal.path}
                    download={activeModal.filename}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8, padding: '8px 14px',
                      fontSize: 13, color: 'var(--text)', textDecoration: 'none',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <i data-lucide="download" style={{ width: 14, height: 14 }} />
                    Download
                  </a>
                  <a
                    href={activeModal.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8, padding: '8px 14px',
                      fontSize: 13, color: 'var(--text)', textDecoration: 'none',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <i data-lucide="external-link" style={{ width: 14, height: 14 }} />
                    Open tab
                  </a>
                  <button
                    onClick={() => setActiveModal(null)}
                    style={{
                      background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                      color: 'var(--muted)', fontSize: 20, lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <iframe
                src={activeModal.path}
                title={activeModal.title}
                style={{ flex: 1, border: 'none', minHeight: 600, background: '#fff' }}
              />
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default Media;
